import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {useInitialEffect} from '../../../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {AdminSuggestionModel} from '../../../../../libs/shared/core/src/models/api/adminSuggestionsModel';
import {DataEventActionsEnum} from '../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {TopBarDataEventObject} from '../../../../../libs/shared/core/src/models/tracking/objectNames/topBarDataEventObject';
import {useDataEventTracking} from '../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {AdminSuggestionCard} from '../../../../settings/suggestions/adminSuggestionCard';
import {AdminSuggestionCardSourcesEnum} from '../../../../settings/suggestions/adminSuggestionTypes';

/*
 * Props.
 */

interface SettingsAdminSuggestionsWidgetProps {
  adminSuggestions: ReadonlyArray<AdminSuggestionModel>;
  onRequestClose: () => void;
  onTabSeen: () => void;
}

const StyledContainerDiv = styled.div`
  width: 600px;
  display: flex;
  flex-direction: column;
  padding: 24px 30px 30px 30px;
  overflow: hidden;
  max-height: 598px;
`;

const StyledHeaderDiv = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 16px;
  height: 30px;
`;

const StyledHeaderText = styled.h3`
  font-weight: ${fontWeights.bold};
  font-size: ${fontSizes.large};
  line-height: 19px;
  color: ${(p) => p.theme.greys.shade90};
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StyledCardsContainer = styled.div`
  overflow-y: auto;
`;

const StyledCardWrapper = styled.div`
  margin: 0px 4px 12px 0;
`;

/*
 * Component.
 */

export const SettingsAdminSuggestionsWidget: FC<SettingsAdminSuggestionsWidgetProps> = (props) => {
  const {adminSuggestions, onRequestClose, onTabSeen} = props;

  const {trackDataEvent} = useDataEventTracking();

  useInitialEffect(() => {
    trackDataEvent(DataEventActionsEnum.VIEW, TopBarDataEventObject.HELP_WIDGET_ADMIN_SUGGESTIONS_TAB, {
      types: adminSuggestions.map((s) => s.type),
    });
    onTabSeen();
  });

  return (
    <StyledContainerDiv>
      <StyledHeaderDiv>
        <StyledHeaderText>
          <Trans id="settings-admin-suggestions-widget-header">Suggestions for your team</Trans>
        </StyledHeaderText>
      </StyledHeaderDiv>
      <StyledCardsContainer>
        {adminSuggestions.map((suggestion) => (
          <StyledCardWrapper key={suggestion.type}>
            <AdminSuggestionCard
              suggestion={suggestion}
              source={{
                type: AdminSuggestionCardSourcesEnum.HELP_WIDGET,
              }}
              onPostApply={onRequestClose}
            />
          </StyledCardWrapper>
        ))}
      </StyledCardsContainer>
    </StyledContainerDiv>
  );
};
